<section class="patient-detail">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h1 class="state-panel__title">Loading patient…</h1>
    <p class="state-panel__message">Hang tight while we fetch this patient record.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <span
      class="state-panel__icon"
      [class.state-panel__icon--error]="!notFound()"
      aria-hidden="true"
    >
      <app-icon
        [name]="errorIcon()"
        size="custom"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </span>
    <h1 class="state-panel__title">{{ errorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button state-panel__button fw-600" routerLink="/access-controls">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Access &amp; Controls</span>
    </a>
  </div>
  } @else if (patient(); as patient) {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a class="breadcrumb__link fw-600" routerLink="/" aria-label="Return to the dashboard"
      >Dashboard</a
    >
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <a class="breadcrumb__link fw-600" routerLink="/access-controls">Access &amp; Controls</a>
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <a class="breadcrumb__link fw-600" routerLink="/access-controls/patients">Patient Results</a>
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">{{ patient.displayName }}</span>
  </nav>

  <header class="page-header flex align-start justify-between gap-16 mb-20">
    <div class="patient-identity flex align-center gap-16">
      <span class="patient-identity__avatar" aria-hidden="true">{{ initials() }}</span>
      <div>
        <div class="page-header__title-row flex align-center flex-wrap gap-12">
          <h1 id="page-title" class="page-title fs-22 fw-700 m-0">{{ patient.displayName }}</h1>
          <span class="page-header__dob">DOB: {{ dateOfBirth() }}</span>
        </div>
        <div class="page-header__meta flex align-center flex-wrap gap-8 mt-5">
          <!-- Live off `GET .../PortalAccess`. It stays neutral and labelled "Status unavailable"
               until that read lands, rather than borrowing the Active treatment — a green "Active"
               chip on a blocked account is the worst failure this screen could have. -->
          <span class="badge {{ accessStatusBadge().class }}" [attr.title]="accessStatusTitle()"
            >{{ accessStatusBadge().label }}</span
          >
          <span class="page-header__identity">{{ identitySummary() }}</span>
        </div>
      </div>
    </div>

    <button type="button" class="secondary-button button--compact" (click)="openModal()">
      <app-icon
        name="manage_search"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Search Patient</span>
    </button>
  </header>

  <!-- The block in force, stated once for the whole page before any card: it is the one thing
       about this patient a staff member must not miss. The card below carries the full record. -->
  @if (blockBannerDetail(); as detail) {
  <div class="blocked-banner flex gap-8" role="note">
    <app-icon
      class="blocked-banner__icon"
      name="block"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <p class="m-0">
      <span class="blocked-banner__title">Portal access is currently blocked.</span>
      This account cannot log in.
      <span class="blocked-banner__detail">{{ detail }}</span>
    </p>
  </div>
  }

  <div class="detail-grid">
    <section class="info-card" aria-labelledby="account-info-title">
      <div class="info-card__header flex align-center gap-8">
        <app-icon
          class="info-card__icon"
          name="assignment_ind"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <h2 id="account-info-title" class="info-card__title">Account Info</h2>
      </div>

      <div class="info-card__body">
        <dl class="field-list flex flex-col gap-10">
          <div class="flex flex-col gap-2">
            <dt>Portal Account Email</dt>
            <dd>{{ emailAddress() }}</dd>
          </div>
          <!-- `field-list__pending` greys the dash, so it is applied per value rather than per row:
               a loaded account can still have a null field (a patient with no login of their own),
               and that dash should read as pending in the same way. -->
          <div class="flex flex-col gap-2">
            <dt>Account Created</dt>
            <dd [class.field-list__pending]="accountCreated() === notAvailable">
              {{ accountCreated() }}
            </dd>
          </div>
          <!-- Login history has no source on either payload, so these three are dashed outright
               rather than bound to a value. They are kept on the card because they are what staff
               are asked for after a disclosure complaint, and the card should show where they will
               sit; the note below says plainly that they are not answered yet. -->
          <div class="flex flex-col gap-2">
            <dt>Last Login</dt>
            <dd class="field-list__pending">{{ notAvailable }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Total Sessions (30 days)</dt>
            <dd class="field-list__pending">{{ notAvailable }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Print / Export Events</dt>
            <dd class="field-list__pending">{{ notAvailable }}</dd>
          </div>
        </dl>

        <p class="pending-note fs-12">
          Login and print/export history isn't on the patient's access record yet.
        </p>
      </div>
    </section>

    <section class="info-card" aria-labelledby="portal-access-title">
      <div class="info-card__header flex align-center gap-8">
        <app-icon
          class="info-card__icon"
          name="lock"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <h2 id="portal-access-title" class="info-card__title">Portal Access Control</h2>
      </div>

      <div class="info-card__body flex flex-col gap-16">
        <!-- The switch reports the access state and is the only control that changes it, in both
             directions: on blocks, off restores. A real checkbox with role="switch" rather than a
             styled div, so assistive tech announces the on/off state itself.

             `disabled` until the access read lands, because until then the screen doesn't know
             which direction a flip would be (and, while unblocked, if the reason lookup failed).
             While a write is in flight it is only `aria-disabled`, so focus stays on it; the
             handler refuses the flip. A flip *on* without a reason goes to the select instead. -->
        <div class="toggle-field flex align-center gap-10">
          <input
            id="portal-access-toggle"
            class="toggle-field__input"
            type="checkbox"
            role="switch"
            [checked]="isBlocked()"
            [disabled]="isToggleLocked()"
            [attr.aria-disabled]="isWriting() ? 'true' : null"
            aria-describedby="portal-access-state"
            (change)="onPortalAccessToggle($event)"
          />
          <label class="toggle-field__text" for="portal-access-toggle">
            <span class="toggle-field__label">Block Portal Access</span>
            <span
              id="portal-access-state"
              class="toggle-field__sublabel flex align-center gap-8"
              [class.toggle-field__sublabel--blocked]="isBlocked()"
              [class.toggle-field__sublabel--restored]="restoreConfirmed()"
            >
              <!-- The progress lives on the switch, since it is the control that was pressed. The
                   same ring the buttons use, in the sublabel's own colour. -->
              @if (isWritingAccess()) {
              <span class="button-spinner" aria-hidden="true"></span>
              }
              <span>{{ accessSublabel() }}</span>
            </span>
          </label>
        </div>

        <!-- A block or restore refused because someone else got there first. Card-level, because
             the refreshed state usually swaps which of the slots below is on screen. -->
        @if (accessChangedError(); as error) {
        <p class="block-action__error m-0 fs-12" role="alert">{{ error }}</p>
        }

        <!-- The block on file, above the controls rather than below them: who
             cut this patient off and why is what a staff member needs before deciding to change
             it. The error family (tinted fill, accent border) so it reads as a restriction in
             force rather than as one more field group inside the card. -->
        @if (block()) {
        <div class="block-summary" role="group" aria-label="Current block">
          <div class="block-summary__header flex align-center gap-8">
            <app-icon
              class="block-summary__icon"
              name="block"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span class="block-summary__title">Portal access blocked</span>
          </div>

          <dl class="block-summary__fields field-list flex flex-col gap-10">
            <div class="flex flex-col gap-2">
              <dt>Reason On File</dt>
              <!-- The field staff came to this card to read, so it carries the accent the rest of
                   the record doesn't — except when there isn't one, where the dash stays muted. -->
              <dd
                [class.block-summary__reason]="blockedReason() !== notAvailable"
                [class.field-list__pending]="blockedReason() === notAvailable"
              >
                {{ blockedReason() }}
              </dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Blocked On</dt>
              <dd>{{ blockedOn() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Blocked By</dt>
              <dd>
                <span class="block-summary__staff flex align-center gap-8">
                  <!-- Dropped rather than dashed when the admin row couldn't be resolved: an
                       initials chip over a placeholder name would put a face on nobody. -->
                  @if (blockedByInitials(); as initials) {
                  <span class="block-summary__avatar" aria-hidden="true">{{ initials }}</span>
                  }
                  <span>{{ blockedBy() }}</span>
                </span>
              </dd>
            </div>
            @if (blockNotes(); as notes) {
            <!-- "Comment", as the field it was typed into is labelled; the API calls it `notes`. -->
            <div class="flex flex-col gap-2">
              <dt>Comment</dt>
              <dd>{{ notes }}</dd>
            </div>
            }
          </dl>
        </div>
        }

        <!-- What sits below the switch, if anything. Nothing while the access read is outstanding:
             the screen cannot know whether flipping the switch would block or restore, so it has
             nothing to offer and the switch is already locked and says so. Only a failed read adds
             a line, because "we could not find out" is worth more than three words. -->
        @if (accessUnavailableReason(); as reason) { @if (accessLoadFailed()) {
        <p class="block-action__error m-0 fs-12" role="alert">{{ reason }}</p>
        } } @else if (isBlocked()) {
        <!-- No Restore button: the switch above is the control, and turning it off fires the
             restore straight away — the action is reversible, so no second confirmation stands
             between a staff member and undoing a wrong block. What is left here is the sentence
             the switch can't carry: a restore lifts the block and nothing else, so a proxy whose
             link was revoked stays revoked, and staff shouldn't promise a caregiver otherwise. -->
        <div class="block-action flex flex-col align-start gap-4">
          @if (restoreError(); as error) {
          <p class="block-action__error m-0 fs-12" role="alert">{{ error }}</p>
          } @else {
          <p class="block-action__helper m-0">
            Turning this off lets the patient and any active proxy sign in again. Revoked proxy
            access stays revoked.
          </p>
          }
        </div>
        } @else {
        <div class="flex flex-col gap-12">
          <div class="flex flex-col gap-4">
            <label class="field-label" for="block-reason">Block Reason</label>
            <select
              id="block-reason"
              class="field-select"
              [class.field-select--invalid]="reasonRequiredShown()"
              [value]="blockReasonTypeId() ?? ''"
              [disabled]="reasonTypesLoadFailed()"
              [attr.aria-invalid]="reasonRequiredShown() ? 'true' : null"
              [attr.aria-describedby]="
              reasonTypesLoadFailed()
                ? 'block-reasons-error'
                : blockError()
                  ? 'block-error'
                  : blockReasonTypeId() === null
                    ? 'block-reason-hint'
                    : null
            "
              (change)="onBlockReasonChange($event)"
            >
              <option value="">Select reason…</option>
              <!-- `selected` as well as the select's own `[value]`: on the first render the options
                 do not exist yet when the select's value is written, so an account that loads
                 already blocked would show the placeholder over its own reason. -->
              @for (reason of blockReasonTypes(); track reason.patientPortalAccessBlockReasonTypeId)
              {
              <option
                [value]="reason.patientPortalAccessBlockReasonTypeId"
                [selected]="reason.patientPortalAccessBlockReasonTypeId === blockReasonTypeId()"
              >
                {{ reason.description }}
              </option>
              }
            </select>

            <!-- Under the select rather than under the switch: it covers both a reason that is still
               missing and a refused save, and the select is the control either one sends a staff
               member back to. `alert` so it is announced — a silent inline error is one a
               screen-reader user leaves the card over. -->
            @if (reasonTypesLoadFailed()) {
            <p id="block-reasons-error" class="block-action__error m-0 fs-12" role="alert">
              Block reasons couldn't be loaded. Reload to try again.
            </p>
            } @else if (blockError(); as error) {
            <p id="block-error" class="block-action__error m-0 fs-12" role="alert">{{ error }}</p>
            } @else if (blockReasonTypeId() === null) {
            <p id="block-reason-hint" class="block-reason__hint m-0">
              Choose a reason before turning on Block Portal Access.
            </p>
            }
          </div>

          <!-- Always on screen, so staff can add context to any block; only whether it is required
             changes, and that follows the reason: "Other" says nothing on its own, so it has to
             come with an explanation. The required state shows as soon as "Other" is picked,
             before any submit, and clears the moment there is a comment or a different reason.
             The asterisk is decoration; `aria-required` is what assistive tech reads. -->
          <div class="flex flex-col gap-4">
            <label class="field-label" for="block-comment">
              {{ isCommentRequired() ? 'Comment' : 'Comment (optional)' }} @if (isCommentRequired())
              {
              <span class="field-label__required" aria-hidden="true">*</span>
              }
            </label>
            <textarea
              id="block-comment"
              class="field-textarea"
              rows="3"
              [class.field-textarea--invalid]="isCommentMissing()"
              [value]="blockComment()"
              [maxLength]="blockCommentMaxLength"
              [disabled]="reasonTypesLoadFailed()"
              [placeholder]="
              isCommentRequired()
                ? 'Required - describe the reason for blocking'
                : 'Add context for the audit log…'
            "
              [attr.aria-required]="isCommentRequired() ? 'true' : null"
              [attr.aria-invalid]="isCommentMissing() ? 'true' : null"
              [attr.aria-describedby]="
              isCommentMissing() ? 'block-comment-error block-comment-hint' : 'block-comment-hint'
            "
              (input)="onBlockCommentInput($event)"
            ></textarea>
            @if (isCommentMissing()) {
            <p id="block-comment-error" class="block-action__error m-0 fs-12">
              {{ commentRequiredHint }}
            </p>
            }
            <!-- The API's rule for this field: it lands in the block history and the audit log,
                 which are not the place for patient identifiers. Always shown, because "Other"
                 forces free text and that is exactly when one gets typed. -->
            <p id="block-comment-hint" class="block-comment__hint m-0">
              Don't include patient identifiers (name, DOB, SSN, MRN).
            </p>
          </div>
        </div>
        }
      </div>
    </section>

    <section class="info-card detail-grid__full" aria-labelledby="proxy-access-title">
      <div class="info-card__header flex align-center gap-8">
        <app-icon
          class="info-card__icon"
          name="person_add"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <h2 id="proxy-access-title" class="info-card__title">Proxy / Caregiver Access</h2>
      </div>

      <!-- Flush body: the rows run the full width of the card and are separated by hairlines
           rather than sitting as boxes inside a padded body, so a caregiver list reads as one
           block. Each row carries the padding the body would otherwise apply. -->
      <div class="info-card__body info-card__body--flush">
        @if (proxyRows().length) {
        <ul class="proxy-list">
          @for (proxy of proxyRows(); track proxy.id) {
          <li
            class="proxy-row flex align-center flex-wrap gap-12"
            [class.proxy-row--aging-out]="proxy.ageOut === 'soon'"
            [class.proxy-row--overdue]="proxy.ageOut === 'overdue'"
            [class.proxy-row--revoked]="proxy.isRevoked"
          >
            <div class="proxy-row__identity flex flex-col gap-2">
              <span class="proxy-row__name fw-600">{{ proxy.displayName }}</span>
              <span class="proxy-row__meta"
                >{{ proxy.meta }}@if (proxy.metaEmphasis) {<strong
                  class="proxy-row__emphasis"
                  [class.proxy-row__emphasis--overdue]="proxy.ageOut === 'overdue'"
                  >{{ proxy.metaEmphasis }}</strong
                >}</span
              >
            </div>

            <div class="proxy-row__actions flex align-center gap-10">
              <!-- At most one chip per row. An ending grant shows its age-out chip in place of
                   "Active"; a revoked link has none, since its "Revoked" button says it. -->
              @if (proxy.statusLabel) {
              <span class="badge {{ proxy.statusClass }}">{{ proxy.statusLabel }}</span>
              }

              <!-- Two steps, unlike the other row actions. Revoking is terminal — re-granting
                   takes a new proxy verification request — and when this is the proxy's last
                   active link it disables their portal account outright, so the consequence is
                   stated in a dialog before the click that causes it. The row itself is left
                   alone while that question is open: it is the thing being asked about. -->
              @if (proxy.isRevoking) {
              <span class="proxy-row__requested fs-11">Revoking…</span>
              } @else if (proxy.isRevoked) {
              <!-- A closed link keeps an inert "Revoked" in the button's place rather than losing
                   it. `aria-disabled` rather than `disabled`, so focus can land here after a
                   confirmed revoke; there is no click handler for it to ignore. -->
              <button
                type="button"
                class="revoke-button revoke-button--revoked"
                [attr.data-revoke]="proxy.id"
                aria-disabled="true"
                [attr.aria-label]="'Proxy access revoked for ' + proxy.displayName"
              >
                Revoked
              </button>
              } @else if (proxy.canRevoke) {
              <button
                type="button"
                class="revoke-button"
                [attr.data-revoke]="proxy.id"
                [disabled]="isWriting()"
                [attr.aria-label]="'Revoke proxy access for ' + proxy.displayName"
                (click)="onRevokeProxy(proxy.id)"
              >
                Revoke
              </button>
              }
            </div>

            <!-- On the row that failed, not above the list: with several caregivers on screen a
                 floating message does not say which one still has access. -->
            @if (proxy.revokeError; as error) {
            <p class="proxy-row__error m-0 fs-11" role="alert">{{ error }}</p>
            }
          </li>
          }
        </ul>
        } @else if (hasPortalAccess()) {
        <!-- A sourced empty list, which is a different statement from the one below it: this says
             nobody holds proxy access, not that the screen cannot tell. -->
        <p class="empty-message proxy-message">
          No proxy or caregiver access on this account. Pending requests are on the
          <a class="inline-link fw-600" routerLink="/requests/proxy-verifications"
            >Proxy Verifications</a
          >
          queue.
        </p>
        } @else if (accessLoadFailed()) {
        <!-- Distinguished from the empty list above and from the in-flight state below: an empty
             caregiver card must never be read as "nobody holds access" when the screen never
             found out. -->
        <p class="empty-message proxy-message" role="status">
          Proxy and caregiver access for this patient couldn't be loaded. Reload the page to try
          again — pending and recently decided proxy requests are on the
          <a class="inline-link fw-600" routerLink="/requests/proxy-verifications"
            >Proxy Verifications</a
          >
          queue.
        </p>
        } @else {
        <p class="empty-message proxy-message" role="status" aria-live="polite">
          Loading proxy and caregiver access…
        </p>
        }

        <!-- The rule that governs the rows above, true whether or not this screen can list them. -->
        <p class="proxy-footnote flex gap-8 fs-12">
          <app-icon
            class="proxy-footnote__icon"
            name="info"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span
            >When a patient turns 18, proxy access granted while they were a minor is automatically
            revoked and a new portal account is created for the patient.</span
          >
        </p>
      </div>
    </section>
  </div>
  }

  <!-- The confirmation that a write went through, or why a block was not sent.

       The live region is rendered unconditionally: a role="status" element inserted together with
       its text is not reliably announced, so the visible chip below is hidden from assistive tech
       and this region carries the message instead. A refused block raises it to role="alert" so it
       isn't queued behind whatever the reader is already saying. The live politeness follows the
       role: a fixed "polite" would override the assertive one an alert implies. -->
  <div
    class="sr-only"
    [attr.role]="toastVariant() === 'error' ? 'alert' : 'status'"
    [attr.aria-live]="toastVariant() === 'error' ? 'assertive' : 'polite'"
  >
    {{ toastMessage() }}
  </div>

  @if (toastMessage(); as message) {
  <div
    class="toast flex align-center gap-8"
    [class.toast--error]="toastVariant() === 'error'"
    aria-hidden="true"
  >
    <app-icon
      class="toast__icon"
      [name]="toastVariant() === 'error' ? 'warning' : 'check_circle'"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span class="toast__text">{{ message }}</span>
  </div>
  }
</section>

<!-- The revoke confirmation. `data-revoke` on the row's button is the return focus target, which
     is why this dialog replaced the in-row prompt rather than the button itself. -->
@if (revokeConfirmProxy(); as proxy) {
<div class="modal-backdrop" role="presentation" (click)="onCancelRevokeProxy()">
  <app-revoke-proxy-access-modal
    [proxyName]="proxy.displayName"
    (confirmed)="onConfirmRevokeProxy(proxy.id)"
    (cancelled)="onCancelRevokeProxy()"
    (click)="$event.stopPropagation()"
  ></app-revoke-proxy-access-modal>
</div>
} @if (isModalOpen()) {
<div class="modal-backdrop" role="presentation" (click)="closeModal()">
  <!-- `stopPropagation` keeps a click inside the dialog from reaching the scrim's close handler. -->
  <app-patient-search-modal
    subtitle="Enter details to find a patient and manage their portal access."
    [minimumCriteria]="minimumCriteria"
    (applied)="onPatientSearch($event)"
    (cleared)="onPatientSearchCleared()"
    (closed)="closeModal()"
    (click)="$event.stopPropagation()"
  ></app-patient-search-modal>
</div>
}
